<script setup lang="ts">
import { computed, ref, useSlots, watch } from 'vue';
import { useRoleCheck } from '@/composables/useRoleCheck';
import type { Orderable, Pagination, ActionMenuItem, MoveDirection, MoveState } from '@/services/types/api.types';
import { useI18n } from 'vue-i18n';
import { Constants } from '@/constants';
import TableActionsMenu from '@/components/TableActionsMenu.vue';

const DIRECTIONS: MoveDirection[] = ['up', 'down'];

const { t } = useI18n();
const slots = useSlots();

// REFS
const search = ref('');
const searchValue = ref<string | null>(null);

interface ReorderableTableProps {
  rows: Orderable[] | unknown[];
  columns: any[];
  title?: string;
  itemKey: string;
  loading?: boolean | undefined;
  hideBottom?: boolean | undefined;
  disableOrderable?: boolean;
  disableDeleteButton?: boolean;
  disableAddButton?: boolean;
  actionMenuItems?: ActionMenuItem[] | undefined;
  filter?: string | any | undefined;
  filterMethod?:
    | ((
        rows: readonly any[],
        terms: string | any,
        cols: readonly any[],
        getCellValue: (col: any, row: any) => any,
      ) => readonly any[])
    | undefined;
  paginationEnabled?: boolean;
  pagination?: Pagination;
  searchEnabled?: boolean | undefined;
  // A move the parent refuses, beyond the first and last rows: its arrow is grayed out, and `reason` is its tooltip.
  getMoveState?: (row: any, direction: MoveDirection) => MoveState;
}

// PROPS
const props = defineProps<ReorderableTableProps>();

// EMITS
const emit = defineEmits(['reorder', 'row-click', 'add-row', 'delete-row', 'request', 'actions']);

// COMPUTED
const columns = computed(() => {
  const cols = [...props.columns];
  if (!props.disableOrderable) cols.push({ name: 'move-up', label: t('global.move-up') });
  if (computedActionMenuItems.value.length > 0) cols.push({ name: 'actions', label: t('global.actions') });
  return cols;
});
const hasFilters = computed(() => !!slots.filters);

// The state of both arrows of each row, by the row's key: the ends of the list, then what the parent refuses.
const moveStates = computed(() => {
  const states = new Map<string, MoveState>();
  props.rows.forEach((row: any, index) => {
    for (const direction of DIRECTIONS) {
      const isEdge = (direction === 'up' && index === 0) || (direction === 'down' && index === props.rows.length - 1);
      states.set(
        moveKey(row, direction),
        isEdge ? { isBlocked: true } : (props.getMoveState?.(row, direction) ?? { isBlocked: false }),
      );
    }
  });
  return states;
});

// FUNCTIONS
const moveKey = (row: any, direction: MoveDirection) => `${row[props.itemKey]}:${direction}`;
const moveState = (row: any, direction: MoveDirection) => moveStates.value.get(moveKey(row, direction));

// A disabled arrow takes no focus and its tooltip opens on hover only, so a screen reader learns why a move is refused
// from the arrow's name.
const moveLabel = (row: any, direction: MoveDirection) => {
  const move = t(`global.reorder.${direction}`);
  const reason = moveState(row, direction)?.reason;
  return reason ? t('global.reorder.blocked', { move, reason }) : move;
};

const handleReorder = (index: number, direction: MoveDirection) => {
  const targetIndex = index + (direction === 'up' ? -1 : +1);
  if (targetIndex < 0 || targetIndex >= props.rows.length) return;
  if (moveState(props.rows[index], direction)?.isBlocked) return;

  const source = props.rows[index] as Orderable;
  const target = props.rows[targetIndex] as Orderable;

  if (!source.order_num || !target.order_num) return;

  const sourceOrderNum = source.order_num;
  const targetOrderNum = target.order_num;

  source.order_num = targetOrderNum;
  target.order_num = sourceOrderNum;

  emit('reorder', source, target);
};

const computedActionMenuItems = computed<ActionMenuItem[]>(() => {
  const actionMenuItems = Array.from(props.actionMenuItems ?? []);
  if (!props.disableDeleteButton) {
    actionMenuItems.push({ action: 'delete-row', label: t('global.label.delete') });
  }
  // Filter items by role check
  return actionMenuItems.filter((item) => {
    if (item.requiresSuperadmin || item.rolesAllowed) {
      const { isUserAuthorized } = useRoleCheck({
        requiresSuperadmin: item.requiresSuperadmin,
        rolesAllowed: item.rolesAllowed,
      });
      return isUserAuthorized.value;
    }
    return true;
  });
});

const paginationTable = ref({
  page: props.pagination?.page || 1,
  rowsPerPage: props.pagination?.rowsPerPage || Constants.Pagination.DefaultRowsPerPage,
  rowsNumber: props.pagination?.rowsNumber || 0,
});

// WATCH
watch(
  () => props.pagination,
  (newVal) => {
    if (newVal) {
      paginationTable.value = {
        page: newVal.page,
        rowsPerPage: newVal.rowsPerPage,
        rowsNumber: newVal.rowsNumber,
      };
    }
  },
  { immediate: true },
); // Ensures it runs on component mount

const requestPagination = (data: any) => {
  const pagination = data.pagination as Pagination;
  paginationTable.value.rowsPerPage = pagination.rowsPerPage;
  paginationTable.value.page = pagination.page;
  emit('request', { ...paginationTable.value, q: searchValue.value });
};

const fetchData = () => {
  if (search.value.length < 3) return;
  const q = search?.value;
  searchValue.value = q;
  emit('request', {
    page: 1,
    rowsPerPage: props.pagination?.rowsPerPage || Constants.Pagination.DefaultRowsPerPage,
    q,
  });
};

const handleSearchInput = (value: any) => {
  if (!value) {
    const q = null;
    searchValue.value = q;
    emit('request', {
      page: 1,
      rowsPerPage: props.pagination?.rowsPerPage || Constants.Pagination.DefaultRowsPerPage,
      q,
    });
  }
};

const handleActions = (event: { action: string; payload: { row: object } }) => {
  if (event.action === 'delete-row') {
    emit('delete-row', event.payload.row, searchValue.value);
  } else {
    emit('actions', event);
  }
};
</script>
<template>
  <q-table
    class="reorderable-table__q-table"
    flat
    :rows="props.rows"
    :columns="columns"
    @row-click="(_, row) => emit('row-click', row)"
    @request="requestPagination"
    id="q-table"
    :loading="props.loading"
    :filter="props.filter"
    :filter-method="props.filterMethod"
    :hide-bottom="props.hideBottom"
    :rows-per-page-options="[5, 10, 15, 20, 25, 50]"
    v-model:pagination="paginationTable"
    :hide-pagination="!props.paginationEnabled"
    wrap-cells
  >
    <template v-slot:top>
      <div>
        <div class="q-table__title">{{ $props.title }}</div>
        <q-space />
        <q-input
          class="search-box"
          v-bind:class="!disableAddButton ? 'q-mr-lg' : ''"
          v-model="search"
          outlined
          dense
          :clearable="!!searchValue"
          :placeholder="t('global.search')"
          @keyup.enter="fetchData"
          v-if="searchEnabled"
          @update:model-value="handleSearchInput"
        >
          <template v-slot:append>
            <q-btn icon="search" flat round dense @click="fetchData" :disable="!search || search.length < 3" />
          </template>
        </q-input>
        <q-btn fab-mini icon="add" color="accent" @click.stop="emit('add-row')" v-if="!disableAddButton" />
      </div>
      <div v-if="hasFilters">
        <slot name="filters" />
      </div>
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <table-actions-menu
          :row="props.row"
          :action-menu-items="computedActionMenuItems"
          @click.stop
          @actions="handleActions"
        />
      </q-td>
    </template>
    <template v-slot:body-cell-move-up="props">
      <q-td :props="props">
        <q-btn-group flat>
          <!-- a disabled button gets no hover, so the tooltip of a blocked move sits on its wrapper -->
          <span v-for="direction in DIRECTIONS" :key="direction" @click.stop>
            <q-btn
              @click.stop="handleReorder(props.rowIndex, direction)"
              :icon="direction === 'up' ? 'expand_less' : 'expand_more'"
              :aria-label="moveLabel(props.row, direction)"
              :disable="moveState(props.row, direction)?.isBlocked"
              :class="{ 'disabled-btn': moveState(props.row, direction)?.isBlocked }"
              flat
              dense
            />
            <q-tooltip v-if="moveState(props.row, direction)?.reason" class="reorderable-table__tooltip">
              {{ moveState(props.row, direction)?.reason }}
            </q-tooltip>
          </span>
        </q-btn-group>
      </q-td>
    </template>
    <template v-for="slotName in Object.keys(slots)" :key="slotName" v-slot:[slotName]="props">
      <slot :name="slotName" v-bind="props" />
    </template>
  </q-table>
</template>
<style lang="scss">
.reorderable-table__q-table {
  & > .q-table__top {
    align-items: start;
    flex-direction: column;
    & > div {
      display: flex;
      flex-direction: row;
      width: 100%;
    }
  }
}
.reorderable-table__tooltip {
  font-size: 14px;
  max-width: 320px;
}
.search-box {
  width: 220px;
}
</style>
